import { type TApiResponse } from "@/types/api";
import { handleAsync, withRetry } from "@/utils";

import { ApiService, type TApiServiceError, type TApiServiceOptions } from "../apiService";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import type {
  TDealNetworkFounderRecommendationCard,
  TDealNetworkFounderRecommendationsQuery,
  TDealNetworkIntroductionRequest,
  TDealNetworkIntroductionRequestCreate,
  TUpdateFounderRecommendationPayload,
} from "./dealNetwork.types";
import {
  type TCompanyRecommendation,
  type TDealNetworkConnectRequest,
  type TUpdateCompanyRecommendationPayload,
} from "./dealNetwork.types";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});
const publicApiService = new ApiService();

class DealNetworkService {
  private rootEndpoint = "deal-network/company-recommendations" as const;

  getCompanyRecommendations(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyRecommendation[]>>(() =>
      apiService.get(`${this.rootEndpoint}`, {
        searchParams: {
          company_id: companyId,
        },
        ...options,
      })
    );
  }

  updateCompanyRecommendation(id: string, payload: TUpdateCompanyRecommendationPayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyRecommendation>>(() =>
      apiService.patch(`${this.rootEndpoint}/${id}`, payload, options)
    );
  }

  refreshFounderRecommendations(companyId: string, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    queryParams.append("company_id", companyId);
    const queryString = queryParams.toString();
    const endpoint = `deal-network/founder-recommendations/refresh?${queryString}`;

    return handleAsync<TApiServiceError, TApiResponse<unknown>>(() =>
      apiService.post<TApiResponse<unknown>, undefined>(endpoint, undefined, options)
    );
  }

  getFounderRecommendations(
    companyId: string,
    params?: TDealNetworkFounderRecommendationsQuery,
    options?: TApiServiceOptions
  ) {
    const queryParams = new URLSearchParams();
    queryParams.append("company_id", companyId);
    if (params?.dismissed !== undefined) queryParams.append("dismissed", String(params.dismissed));
    if (params?.shortlisted !== undefined) queryParams.append("shortlisted", String(params.shortlisted));

    const queryString = queryParams.toString();
    const endpoint = `deal-network/founder-recommendations?${queryString}`;

    return handleAsync<TApiServiceError, TApiResponse<TDealNetworkFounderRecommendationCard[]>>(() =>
      apiService.get<TApiResponse<TDealNetworkFounderRecommendationCard[]>>(endpoint, options)
    );
  }

  updateFounderRecommendation(
    recommendationId: string,
    payload: TUpdateFounderRecommendationPayload,
    options?: TApiServiceOptions
  ) {
    const endpoint = `deal-network/founder-recommendations/${recommendationId}`;
    return handleAsync<TApiServiceError, TApiResponse<TDealNetworkFounderRecommendationCard>>(() =>
      apiService.patch<TApiResponse<TDealNetworkFounderRecommendationCard>, TUpdateFounderRecommendationPayload>(
        endpoint,
        payload,
        options
      )
    );
  }

  createIntroductionRequest(body: TDealNetworkIntroductionRequestCreate, options?: TApiServiceOptions) {
    const endpoint = "deal-network/introduction-requests";
    return handleAsync<TApiServiceError, TApiResponse<TDealNetworkIntroductionRequest>>(() =>
      apiService.post<TApiResponse<TDealNetworkIntroductionRequest>, TDealNetworkIntroductionRequestCreate>(
        endpoint,
        body,
        options
      )
    );
  }

  createRecommendationConnectRequest(recommendationId: string, options?: TApiServiceOptions) {
    const endpoint = `${this.rootEndpoint}/${recommendationId}/connect-requests`;
    return handleAsync<TApiServiceError, TApiResponse<TDealNetworkConnectRequest>>(() =>
      withRetry(() =>
        apiService.post<TApiResponse<TDealNetworkConnectRequest>, undefined>(endpoint, undefined, options)
      )
    );
  }

  createFounderRecommendationConnectRequest(recommendationId: string, options?: TApiServiceOptions) {
    const endpoint = `deal-network/founder-recommendations/${recommendationId}/connect-requests`;
    return handleAsync<TApiServiceError, TApiResponse<TDealNetworkConnectRequest>>(() =>
      withRetry(() =>
        apiService.post<TApiResponse<TDealNetworkConnectRequest>, undefined>(endpoint, undefined, options)
      )
    );
  }

  downloadFounderRecommendationsReport(companyId: string, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    queryParams.append("company_id", companyId);
    const queryString = queryParams.toString();
    const endpoint = `deal-network/founder-recommendations/report?${queryString}`;

    return handleAsync<
      TApiServiceError,
      {
        blob: Blob;
        filename: string | null;
      }
    >(async () => {
      const response = await apiService.getRaw(endpoint, options);
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") ?? response.headers.get("Content-Disposition");
      let filename: string | null = null;
      if (disposition) {
        const filenameRegex = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i;
        const filenameMatch = filenameRegex.exec(disposition);
        if (filenameMatch?.[1]) {
          try {
            filename = decodeURIComponent(filenameMatch[1]);
          } catch {
            filename = filenameMatch[1];
          }
        }
      }
      return {
        blob,
        filename,
      };
    });
  }

  createConnectRequestIntroduction(connectRequestId: string, options?: TApiServiceOptions) {
    const endpoint = `deal-network/connect-requests/${connectRequestId}/introductions`;
    return handleAsync<TApiServiceError, TApiResponse<TDealNetworkConnectRequest>>(() =>
      publicApiService.post<TApiResponse<TDealNetworkConnectRequest>, undefined>(endpoint, undefined, options)
    );
  }

  createInvestorConnectRequestIntroduction(connectRequestId: string, options?: TApiServiceOptions) {
    const endpoint = `deal-network/investor-connect-requests/${connectRequestId}/introductions`;
    return handleAsync<TApiServiceError, TApiResponse<TDealNetworkConnectRequest>>(() =>
      publicApiService.post<TApiResponse<TDealNetworkConnectRequest>, undefined>(endpoint, undefined, options)
    );
  }
}

export const dealNetworkService = new DealNetworkService();
